import { useCallback, useEffect, useMemo, useState } from 'react';
import {
	useRecoilState,
	useRecoilValue,
	useResetRecoilState,
	useSetRecoilState,
} from 'recoil';

import { Button, Loader } from '@storybook';
import { validatePhoneNumber } from '@storybook/country-code';
import {
	CaptableGrid,
	INVESTOR_CAPTABLE_DATA,
} from '@storybook/custom-captable';
import {
	AddCapTableMapKeyAndValue,
	RequiredField,
} from '@storybook/custom-captable/states';
import Modal from '@storybook/new-modal/modal';
import { API_URL } from 'constant';
import {
	FundDetailState,
	FundInvestorFormErrorHandlerState,
	FundInvestorModalState,
	FundInvestorState,
	FundInvestors,
	FundsState,
	isFundCallingState,
} from 'global-stores';
import { envHost } from 'helpers';
import { useNetwork, useNotification } from 'hooks';
import {
	getLuckSheetData,
	highlightCaptableCells,
	clearCaptableHighlight,
	isEmailValid,
	isNumber,
	isValidAmount,
	isValidCountryCode,
	isValidDate,
	isValidDecimal,
	isValidName,
	useFormatNumber,
} from 'utils';
import { FundDataForEscrowState, FundEscrow } from 'views/fund-escrow';
import {
	ADD_INVESTOR_ERROR,
	Add_FUND_MAP_CAPTABLE_ROW,
	CAPTABLE_ALL_FIELDS_REQUIRED_MESSAEG,
	Navigate,
} from 'views/funds-pages/constants';
import { ConfigCaptable } from './config-captable';
import { EditFunds } from './edit-funds';
import { JsonToCSV } from './json-to-csv';

export interface IFunds {
	name: string;
	investor: number;
	pending: string;
	moneyRaised: string;
	fundingRound: string;
	totalMoneyRaised: string;
	postMoneyValuation: string;
	id: string;
}

export interface IFundHeaderApi {
	openAdd: () => void;
	openEdit: () => void;
}

/* The modal's own skin travels with the component that owns the modal, so it
   arrives wherever this header is used. tokens/surface come from the sv2 page. */
import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
import '../deck/captable-modal.scss';

/* The five columns handleAddInvestor treats as mandatory. Shown in the
   instruction strip so the rule is readable before Save rejects a paste. */
const REQUIRED_COLUMNS = [
	'First Name',
	'Last Name',
	'Country Code',
	'Mobile',
	'Email',
];

/** 0-based column index to its spreadsheet letter (0 -> A, 26 -> AA). */
const columnLetter = (c: number): string => {
	let n = c;
	let out = '';
	while (n >= 0) {
		out = String.fromCharCode(65 + (n % 26)) + out;
		n = Math.floor(n / 26) - 1;
	}
	return out;
};

/** Scroll the sheet to a cell and select it. Luckysheet is a global singleton. */
const jumpToCell = (r: number, c: number) => {
	const ls = (window as any)?.luckysheet;
	try {
		ls?.setRangeShow?.({ row: [r, r], column: [c, c] });
	} catch {
		/* the grid is unmounted (step 2) — nothing to scroll */
	}
};

export const FundIconHeader = ({
	fundId,
	isUserPermissionWrite = true,
	chromeless = false,
	api,
}: {
	fundId: string;
	isUserPermissionWrite?: boolean;
	/** render the modals and the wiring, none of the old buttons or stats */
	chromeless?: boolean;
	/** filled with the handlers, so the sv2 page's controls can drive them */
	api?: { current: IFundHeaderApi | null };
}) => {
	//globle states
	const funds = useRecoilValue(FundsState);
	const fundDetailList = useRecoilValue(FundDetailState);
	const setFundDataForEscrow = useSetRecoilState(FundDataForEscrowState);
	const resetList = useResetRecoilState(FundInvestorState);
	const setIsFundCalling = useSetRecoilState(isFundCallingState);
	const [fundInvestorModalState, setFundInvestorModalState] = useRecoilState(
		FundInvestorModalState
	);
	const getInvestors = useRecoilValue(FundInvestors);
	const resetFormErrorHandlerState = useResetRecoilState(
		FundInvestorFormErrorHandlerState
	);
	const selectedValue = useRecoilValue(AddCapTableMapKeyAndValue);
	const requiredField = useRecoilValue(RequiredField);

	//local states
	const [fundDataHead, setFundDataHead] = useState<IFunds>({
		name: '',
		investor: 0,
		pending: '',
		moneyRaised: '',
		fundingRound: '',
		totalMoneyRaised: '',
		postMoneyValuation: '',
		id: '',
	});
	const [isEditModal, setIsEditModal] = useState(false);

	/* What the last save attempt rejected, kept until the next attempt so the
	   reader can work through it. `cells` is handleAddInvestor's own `bad[]`. */
	const [capErrors, setCapErrors] = useState<{
		message: string;
		cells: { r: number; c: number; header: string }[];
	} | null>(null);
	const [isBackFromCap, setIsBackFromCap] = useState(true);

	const [investerIds, setInvesterIds] = useState<any>([]);
	const [navigate, setNavigate] = useState(Navigate.Csv);
	const [getConfigCapTable, setGetConfigCapTable] = useState<any>([]);

	// hooks
	const { errorNotification, successNotification } = useNotification();
	const { numberDecimal } = useFormatNumber();
	const { patch: addInvestor, loading: Loading } = useNetwork();

	const { isModalOpen } = fundInvestorModalState;

	const closeAddInvestorModal = () => {
		setCapErrors(null);
		setFundInvestorModalState({
			...fundInvestorModalState,
			isModalOpen: false,
		});
		setNavigate(Navigate.Csv);
		resetList();
		setIsBackFromCap(true);
		const finalXlsValue: any = window;
		finalXlsValue?.luckysheet.exitEditMode();
		resetFormErrorHandlerState();
	};

	const openAddInvestorModal = () => {
		setCapErrors(null);
		setFundInvestorModalState({
			...fundInvestorModalState,
			isModalOpen: true,
			id: '',
			isEdit: false,
		});
		resetList();
		resetFormErrorHandlerState();
	};

	const INVESTOR_CAPTABLE_HEADER = useMemo(
		() => fundDetailList && fundDetailList[0]?.rawData?.map(item => item.label),
		[fundDetailList]
	);

	const formateCaptableData = useCallback(() => {
		const colsData = fundDetailList
			.filter((i: any) => !i.sessionId)
			.map(item => {
				const { rawData = [] } = item ?? [];
				const formatData = rawData.map(v => v.value);
				return formatData;
			});
		const ids = fundDetailList?.map(item => {
			const { id } = item ?? [];
			return id;
		});
		return {
			colsData,
			ids,
		};
	}, [fundDetailList]);

	const openEditInvestorModal = () => {
		setIsEditModal(true);
		const { colsData = [], ids = [] } = formateCaptableData();
		setInvesterIds(ids);
		const concatInvestors = [INVESTOR_CAPTABLE_HEADER]?.concat(colsData);
		localStorage.setItem(
			'CAPTABLE_HEADER',
			JSON.stringify(concatInvestors ?? [])
		);
	};

	/**
	 * The sv2 page owns the buttons now, but the handlers, their stores and the
	 * localStorage hand-off between the cap-table screens all live here. Rather
	 * than duplicate that (and drift), the handlers are handed up.
	 */
	useEffect(() => {
		if (!api) return;
		api.current = {
			openAdd: openAddInvestorModal,
			openEdit: openEditInvestorModal,
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	});

	const onSaveCaptable = async () => {
		const { rows = [], headers = [] } = getLuckSheetData();
		const payload = {
			capTable: {
				rows,
				headers,
			},
		};
		const response = await addInvestor(`${API_URL.FUNDS}/${fundId}`, payload);
		if (response?._id && !Loading) {
			setIsFundCalling(true);
			successNotification('Added Sucessfully');
			closeAddInvestorModal();
		} else {
			errorNotification(response?.message || ADD_INVESTOR_ERROR);
		}
	};

	const handleAddInvestor = useCallback(async () => {
		if (navigate === Navigate.Csv) {
			const { expectedArray, headers, rows, rowIndices } = getLuckSheetData();
			if (rows.length < 1) {
				errorNotification(CAPTABLE_ALL_FIELDS_REQUIRED_MESSAEG);
				return;
			}

			/**
			 * __simplici_funds_sv2__
			 *
			 * This used to fire one toast per bad row, naming the column in prose
			 * ("a valid Dollar Amount in row 3") and painting nothing — so on a
			 * pasted cap table wide enough to scroll, the reader still had to count
			 * columns to find the cell it meant. Now every offending cell is
			 * collected, painted light red in the sheet itself, and summarised in a
			 * single message that names the columns.
			 *
			 * It also used to CONTINUE on to submit. `isValidData` was reassigned at
			 * the top of every row, so after the loop it described the LAST row only:
			 * a bad row 1 followed by a good row 2 toasted an error and then posted
			 * the invalid data anyway. It now bails whenever anything failed.
			 */
			const columnAt = (name: string) => headers.indexOf(name);
			const bad: { r: number; c: number; header: string }[] = [];
			const flag = (rowIdx: number, header: string) => {
				const c = columnAt(header);
				if (c >= 0) bad.push({ r: rowIdx, c, header });
			};

			const isFilled: boolean[] = [];
			const firstNameIndex = columnAt('First Name');
			const lastNameIndex = columnAt('Last Name');
			const emailIndex = columnAt('Email');
			const countryCodeIndex = columnAt('Country Code');
			const mobileIndex = columnAt('Mobile');
			const dollarsIndex = columnAt('Dollars Invested');
			const numberOfSharesIndex = columnAt('No. of Shares');
			const ppsIndex = columnAt('Price per Share');
			const isssueDateIndex = columnAt('Issue Date');

			rows.forEach((el: any, i: number) => {
				// the sheet row this entry came from — NOT i + 1, blank rows shift it
				const sheetRow = rowIndices?.[i] ?? i + 1;

				// Every field is checked, not just the first that fails, so one pass
				// shows the reader everything they have to correct.
				if (!isValidName(el[firstNameIndex])) flag(sheetRow, 'First Name');
				if (!isValidName(el[lastNameIndex])) flag(sheetRow, 'Last Name');
				if (el[emailIndex]?.length > 0 && !isEmailValid(el[emailIndex]))
					flag(sheetRow, 'Email');
				if (
					el[countryCodeIndex]?.length > 0 &&
					!isValidCountryCode(el[countryCodeIndex])
				)
					flag(sheetRow, 'Country Code');
				if (
					el[mobileIndex]?.length > 0 &&
					!validatePhoneNumber(
						el[mobileIndex],
						el[countryCodeIndex]?.startsWith('+')
							? el[countryCodeIndex]
							: `+${el[countryCodeIndex] ?? ''}`
					).isValid
				)
					flag(sheetRow, 'Mobile');
				if (el[dollarsIndex]?.length > 0 && !isValidDecimal(el[dollarsIndex]))
					flag(sheetRow, 'Dollars Invested');
				if (
					el[numberOfSharesIndex]?.length > 0 &&
					!isNumber(el[numberOfSharesIndex])
				)
					flag(sheetRow, 'No. of Shares');
				if (el[ppsIndex]?.length > 0 && !isValidAmount(el[ppsIndex]))
					flag(sheetRow, 'Price per Share');
				if (el[isssueDateIndex]?.length > 0 && !isValidDate(el[isssueDateIndex]))
					flag(sheetRow, 'Issue Date');

				isFilled.push(
					el.every(
						(fil: string | any[]) => fil?.length > 0 && typeof fil === 'string'
					) && el?.length === headers.length
				);
			});

			if (bad.length) {
				highlightCaptableCells(bad);

				// Name the columns, and the rows only while there are few enough for
				// that to help; past a handful the red cells are the better guide.
				const columns = Array.from(new Set(bad.map(b => b.header)));
				const badRows = Array.from(new Set(bad.map(b => b.r + 1))).sort(
					(a, b) => a - b
				);
				const where =
					badRows.length === 1
						? `row ${badRows[0]}`
						: badRows.length <= 4
						? `rows ${badRows.join(', ')}`
						: `${badRows.length} rows`;
				const which =
					columns.length === 1
						? columns[0]
						: `${columns.slice(0, -1).join(', ')} and ${
								columns[columns.length - 1]
						  }`;
				const message = `Highlighted in red: check ${which} in ${where}. ${
					bad.length
				} cell${bad.length === 1 ? '' : 's'} need${
					bad.length === 1 ? 's' : ''
				} fixing before this can be saved.`;

				/* The bar is the persistent copy — it stays next to the red cells until
				   they are fixed. The toast still fires so the change is not missed by
				   anyone whose eyes are on the grid when Save is pressed. */
				setCapErrors({ message, cells: bad });
				errorNotification(message);
				return;
			}

			// Nothing invalid — drop any red left over from a previous attempt.
			clearCaptableHighlight();
			setCapErrors(null);

			if (isFilled.length > 0 && !isFilled.every((el: boolean) => el)) {
				setCapErrors({ message: CAPTABLE_ALL_FIELDS_REQUIRED_MESSAEG, cells: [] });
				errorNotification(CAPTABLE_ALL_FIELDS_REQUIRED_MESSAEG);
				return;
			}

			const removeArr = ['Last Name'];
			const gridHead = INVESTOR_CAPTABLE_DATA[0]?.filter(
				(e: any) => !removeArr.includes(e)
			);

			const isSame = gridHead?.every((header: any) =>
				expectedArray[0]?.includes(header)
			);

			if (isSame) onSaveCaptable();
			else {
				const { headers = [] } = getLuckSheetData();
				const dropDownOptions = headers?.map((header: string) => ({
					label: header,
					value: header,
				}));

				const result = Add_FUND_MAP_CAPTABLE_ROW.map((item: any) => {
					const Payload = {
						options: dropDownOptions,
					};
					return { ...Payload, ...item };
				});
				setGetConfigCapTable(result);
				setNavigate(Navigate.ConfigCaptable);
			}
		} else {
			const { rows = [] } = getLuckSheetData();
			const payload = {
				capTable: {
					rows,
					headers: selectedValue,
				},
			};
			const response = await addInvestor(`${API_URL.FUNDS}/${fundId}`, payload);

			if (response?._id && !Loading) {
				setIsFundCalling(true);
				successNotification('Added Sucessfully');
				closeAddInvestorModal();
			} else {
				errorNotification(response?.message || ADD_INVESTOR_ERROR);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedValue, navigate, Loading]);

	const closeEditInvestorModal = () => {
		setIsEditModal(false);
		const finalXlsValue: any = window;
		finalXlsValue?.luckysheet.exitEditMode();
	};

	const getTotalValue = useMemo(() => {
		const value = fundDetailList?.reduce((acc: number, crr: any) => {
			const { sessionId, fullStatus = {}, invested } = crr;
			const { payIn, payOut } = fullStatus;
			const price = Number(invested ?? 0);
			if (
				(!sessionId || [payIn, payOut].includes('completed')) &&
				!isNaN(price)
			) {
				return acc + price;
			}
			return acc;
		}, 0);
		return value ?? 0.0;
	}, [fundDetailList]);

	const getPendingValue = useMemo(() => {
		const value = fundDetailList?.reduce((acc: any, crr: any) => {
			const { sessionId, fullStatus = {}, invested } = crr;
			const { payIn, payOut } = fullStatus;
			const price = Number(invested ?? 0);
			if (
				sessionId &&
				![payIn, payOut].includes('completed') &&
				!isNaN(price)
			) {
				return acc + price;
			}
			return acc;
		}, 0);
		return value ?? 0.0;
	}, [fundDetailList]);

	useEffect(() => {
		if (funds) {
			const fundData = funds.find(fund => fund.id === fundId);
			if (fundData) {
				setFundDataHead(fundData);
				setFundDataForEscrow({ ...(fundData as any) });
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [funds, fundId]);

	const modalLabel = useMemo(() => {
		switch (navigate) {
			case 'Csv':
				return 'Add investors';
			default:
				return 'Match your columns';
		}
	}, [navigate]);

	const nextLabel = useMemo(() => {
		return Loading ? (
			<Loader type="loader" dimension={20} className="loader-white" />
		) : (
			'Save'
		);
	}, [Loading]);

	const backLabel = useMemo(() => {
		switch (navigate) {
			case Navigate.Csv:
				return 'Cancel';
			default:
				return 'Back';
		}
	}, [navigate]);

	const renderView = useMemo(() => {
		switch (navigate) {
			case Navigate.Csv:
				return (
					/* The grid is a canvas widget and cannot follow the theme, so it is
					   framed as a sheet of paper inside a shell that does. `.sheet-paper`
					   carries the fixed height Luckysheet measures at create(). */
					<div className="sheet">
						<div className="sheet-paper">
							<div className="luckysheet-wrapper">
								<CaptableGrid isInvestor={isBackFromCap} />
							</div>
						</div>
					</div>
				);
			default:
				return (
					<>
						<div className="dsec">
							<ConfigCaptable capTableHeader={getConfigCapTable ?? []} />
						</div>
						<div className="foot">
							Only the columns we could not match automatically are listed.
						</div>
					</>
				);
		}
	}, [navigate, getConfigCapTable, isBackFromCap]);

	const handleBackBtn = useCallback(() => {
		const { headers, rows } = getLuckSheetData();
		const concatInvestors = [headers]?.concat(rows);
		switch (navigate) {
			case Navigate.Csv:
				return closeAddInvestorModal();
			default:
				setIsBackFromCap(false);
				localStorage.setItem(
					'CAPTABLE_HEADER',
					JSON.stringify(concatInvestors ?? [])
				);
				return setNavigate(Navigate.Csv);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [navigate]);

	const disableNextBtn = useMemo(() => {
		const requiredList = getConfigCapTable?.filter(
			(el: { isRequired: boolean }) => el.isRequired == true
		);
		const isDisabled = requiredList?.some((item: { label: string }) =>
			requiredField.includes(item.label)
		);
		switch (navigate) {
			case Navigate.Csv:
				return false;
			default:
				return isDisabled;
		}
	}, [navigate, getConfigCapTable, requiredField]);

	const renderViewModal = useMemo(() => {
		return (
			<>
				{/* `.dhead`/`.dx` are the shared modal-head primitives. The old
					`user-roles--header` classes are NOT reused here: they belong to the
					User Roles screen and editing them restyles it. */}
				<div className="dhead">
					<div className="dt">
						<h2>{modalLabel}</h2>
						{!!fundDataHead?.name && (
							<span className="vch ns">{fundDataHead.name}</span>
						)}
						<button
							className="dx"
							type="button"
							aria-label="Close"
							onClick={closeAddInvestorModal}
						>
							<i className="ri-close-line" />
						</button>
					</div>
				</div>

				{navigate === Navigate.Csv && (
					<div className="cstrip">
						<span className="ct">
							Paste a cap table anywhere in the grid, or type into it.
						</span>
						<span className="creq">
							Required
							{REQUIRED_COLUMNS.map(c => (
								<b className="vch ns" key={c}>
									{c}
								</b>
							))}
						</span>
						<span className="chint mono">
							Country code <b>1</b> · dates <b>YYYY-MM-DD</b>
						</span>
					</div>
				)}

				{/* The validation summary lives HERE rather than in a toast: a toast
					renders at the top of the viewport, most of a screen from the red
					cells it is describing, and then expires while the problem is still
					on screen. */}
				{!!capErrors && (
					<div className="errbar">
						<i className="ri-error-warning-fill" />
						<span className="et">{capErrors.message}</span>
						{!!capErrors.cells.length && (
							<span className="ecells">
								{capErrors.cells.slice(0, 6).map(c => (
									<button
										className="ecell"
										type="button"
										key={`${c.r}-${c.c}`}
										onClick={() => jumpToCell(c.r, c.c)}
									>
										{`${columnLetter(c.c)}${c.r + 1} · ${c.header}`}
									</button>
								))}
								{capErrors.cells.length > 6 && (
									<span className="ecell">{`+${
										capErrors.cells.length - 6
									} more`}</span>
								)}
							</span>
						)}
					</div>
				)}

				{renderView}

				<div className="dbtns dbtns--footer">
					<button className="dbtn" type="button" onClick={handleBackBtn}>
						{backLabel}
					</button>
					<button
						className="dbtn pri"
						type="button"
						onClick={handleAddInvestor}
						disabled={(disableNextBtn as boolean) || Loading}
					>
						{nextLabel}
					</button>
				</div>
			</>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		modalLabel,
		nextLabel,
		renderView,
		Loading,
		disableNextBtn,
		backLabel,
		navigate,
		capErrors,
		fundDataHead,
	]);

	return (
		<div
			className={`fund-detail-summary${
				chromeless ? ' fund-detail-summary--chromeless' : ''
			}`}
		>
			{!chromeless && <div className="header__name">{fundDataHead.name}</div>}
			<div className="fund-detail--summary">
				{/* __simplici_funds_sv2__: the sv2 page draws its own `.strip` from
					FundDetailState, so this stat block would be the SAME five numbers
					printed twice down the page. Gated in JSX rather than hidden in
					css: `display:none` still mounts it, and these tiles each read
					`getInvestors`/`getTotalValue` off the same list the strip does. */}
				{!chromeless && (
				<div className="Outer_funds_details">
					<div className="count_outer">
						<i className="ri-group-fill"></i>
						<div className="count_container">
							<div className="count_value">{getInvestors.investors}</div>
							<span className="count_label">Investors</span>
						</div>
					</div>
					<div className="count_outer">
						<i className="ri-group-fill money-pending"></i>
						<div className="count_container">
							<div className="count_value">{getInvestors.pendingInvestors}</div>
							<span className="count_label">Pending Investors</span>
						</div>
					</div>
					<div className="count_outer">
						<i className="ri-hand-coin-fill"></i>
						<div className="count_container">
							<div className="count_value">
								${getTotalValue?.toFixed(2) ?? 0.0}
							</div>
							<span className="count_label">Total Money Raised (USD)</span>
						</div>
					</div>
					<div className="count_outer">
						<i className="ri-hand-coin-fill money-pending"></i>
						<div className="count_container">
							<div className="count_value">
								${getPendingValue?.toFixed(2) ?? 0.0}
							</div>
							<span className="count_label">Money Raised Pending (USD)</span>
						</div>
					</div>
					<div className="count_outer">
						<i className="ri-funds-box-fill"></i>
						<div className="count_container">
							<div className="count_value">
								${numberDecimal(Number(fundDataHead.postMoneyValuation) ?? 0)}
							</div>
							<span className="count_label">Post Money Valuation (USD)</span>
						</div>
					</div>
				</div>
				)}

				<>
					<Modal
						isOpen={isModalOpen}
						closeModal={closeAddInvestorModal}
						className={`react-modal_IFunds_Add_Investor sv2-scope sv2-modal captable-modal${
							navigate === Navigate.ConfigCaptable
								? ' captable-modal--map override_modal'
								: ''
						}`}
						modalName="Session modal"
						showHeader={false}
					>
						{renderViewModal}
					</Modal>
				</>
				<EditFunds
					isModalOpen={isEditModal}
					closeAddInvestorModal={closeEditInvestorModal}
					investerIds={investerIds}
					fundId={fundId}
				/>
				{/* __simplici_funds_sv2__: in chromeless mode this component keeps every
					modal, handler and store wiring it has always had, and renders none of
					its own buttons or stats — the sv2 page draws those. FundEscrow and
					JsonToCSV stay mounted because they ARE the escrow modal and the CSV
					downloader, not just triggers for them.

					The `display:none` classes that hid Edit Investor and Escrow are gone
					(§5.2); the env gate that actually means something is kept. */}
				<div
					className={`funds-action-btn-wrapper${
						chromeless ? ' funds-action-btn-wrapper--chromeless' : ''
					}`}
				>
					{!chromeless && fundDetailList?.length > 0 && (
						<Button
							label="Edit Investor"
							handleClick={openEditInvestorModal}
							type="button__filled button__filled--primary"
							disabled={envHost !== 'stage'}
						/>
					)}
					{!chromeless && isUserPermissionWrite && (
						<Button
							label="Add Investor"
							handleClick={openAddInvestorModal}
							type="button__filled button__filled--primary"
						/>
					)}
					{envHost === 'stage' && <FundEscrow />}
					<JsonToCSV />
				</div>
			</div>
		</div>
	);
};
